<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import TicketFormsAPI from 'dashboard/api/ticketForms';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FormCreateModal from './FormCreateModal.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const allForms = ref([]);
const loading = ref(true);
const showCreateModal = ref(false);
const ticketCategories = ref([]);

// UTM generator state: keyed by form.id
const utmExpandedId = ref(null);
const utmParams = ref({
  source: '',
  medium: '',
  campaign: '',
  term: '',
  content: '',
});

async function fetchData() {
  loading.value = true;
  try {
    const [formsRes, catRes] = await Promise.all([
      TicketFormsAPI.get(),
      TicketCategoriesAPI.get(),
    ]);

    const rawForms = formsRes.data?.payload ?? formsRes.data;
    allForms.value = Array.isArray(rawForms) ? rawForms : [];

    const rawCats = catRes.data;
    ticketCategories.value = Array.isArray(rawCats)
      ? rawCats
      : rawCats?.payload || [];
  } catch {
    allForms.value = [];
  } finally {
    loading.value = false;
  }
}

function openCreateModal() {
  showCreateModal.value = true;
}

function navigateToFormBuilder(formId) {
  router.push({
    name: 'forms_settings_edit',
    params: { accountId: accountId.value, formId: String(formId) },
  });
}

async function handleFormCreated(data) {
  showCreateModal.value = false;
  useAlert(t('FORMS_SETTINGS.ALERTS.CREATED'));
  await fetchData();
  navigateToFormBuilder(data.id);
}

async function toggleFormActive(form) {
  try {
    await TicketFormsAPI.update(form.id, {
      ticket_form: { active: !form.active },
    });
    form.active = !form.active;
    useAlert(
      form.active
        ? t('FORMS_SETTINGS.ALERTS.ACTIVATED')
        : t('FORMS_SETTINGS.ALERTS.DEACTIVATED')
    );
  } catch {
    useAlert(t('FORMS_SETTINGS.ALERTS.UPDATE_ERROR'));
  }
}

async function deleteForm(form) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('FORMS_SETTINGS.DELETE_CONFIRM', { name: form.name })))
    return;
  try {
    await TicketFormsAPI.delete(form.id);
    useAlert(t('FORMS_SETTINGS.ALERTS.DELETED'));
    fetchData();
  } catch {
    useAlert(t('FORMS_SETTINGS.ALERTS.DELETE_ERROR'));
  }
}

function getCategoryName(categoryId) {
  const cat = ticketCategories.value.find(c => c.id === categoryId);
  return cat ? `${cat.name} (${cat.code})` : '';
}

// "Category (CODE) › Subcategory" — drops the subcategory part when unset.
function getCategoryLine(form) {
  return [getCategoryName(form.ticket_category_id), form.subcategory_name]
    .filter(Boolean)
    .join(' › ');
}

function getPublicUrl(form) {
  if (!form.identifier) return '';
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/forms/${form.identifier}`;
}

function embedSnippet(form) {
  const src = `${getPublicUrl(form)}?embed=true`;
  return `<iframe src="${src}" title="${form.name}" width="100%" height="720" frameborder="0"></iframe>`;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('FORMS_SETTINGS.ALERTS.COPIED'));
  } catch {
    useAlert(t('FORMS_SETTINGS.ALERTS.COPY_ERROR'));
  }
}

function toggleUtmBuilder(formId) {
  if (utmExpandedId.value === formId) {
    utmExpandedId.value = null;
  } else {
    utmExpandedId.value = formId;
    utmParams.value = {
      source: '',
      medium: '',
      campaign: '',
      term: '',
      content: '',
    };
  }
}

function buildUtmUrl(form) {
  const base = getPublicUrl(form);
  const params = new URLSearchParams();
  Object.entries(utmParams.value).forEach(([key, val]) => {
    if (val) params.set(`utm_${key}`, val);
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

onMounted(fetchData);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('FORMS_SETTINGS.LOADING')"
    :no-records-found="!allForms.length && !loading"
    :no-records-message="$t('FORMS_SETTINGS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('FORMS_SETTINGS.TITLE')"
        :description="$t('FORMS_SETTINGS.DESCRIPTION')"
        feature-name="forms"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('FORMS_SETTINGS.NEW_FORM')"
            @click="openCreateModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-2">
        <div
          v-for="form in allForms"
          :key="form.id"
          class="border border-n-weak rounded-lg p-4 flex items-center gap-4 hover:bg-n-alpha-1 transition-colors"
        >
          <span
            class="w-2.5 h-2.5 rounded-full shrink-0"
            :class="form.active ? 'bg-emerald-500' : 'bg-n-slate-9'"
          />

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-0.5">
              <span class="font-medium text-n-slate-12 truncate">
                {{ form.name }}
              </span>
              <span
                v-if="!form.active"
                class="text-[10px] text-n-slate-9 bg-n-alpha-2 px-1.5 py-0.5 rounded"
              >
                {{ $t('FORMS_SETTINGS.INACTIVE') }}
              </span>
            </div>
            <div class="flex flex-col gap-1 text-xs text-n-slate-10">
              <span v-if="form.form_type === 'contact'">
                {{ $t('FORM_BUILDER.CONTACT_FORM') }}
              </span>
              <span v-else-if="form.ticket_category_id">
                {{ getCategoryLine(form) }}
              </span>
              <div v-if="getPublicUrl(form)" class="flex items-center gap-1">
                <code class="text-[10px] text-n-slate-11 truncate max-w-xs">
                  {{ getPublicUrl(form) }}
                </code>
                <button
                  v-tooltip.top="$t('FORMS_SETTINGS.COPY_URL')"
                  type="button"
                  class="p-0.5 text-n-slate-9 hover:text-n-slate-12 transition-colors"
                  @click.stop="copyToClipboard(getPublicUrl(form))"
                >
                  <span class="i-lucide-copy w-3 h-3" />
                </button>
              </div>
              <div v-if="getPublicUrl(form)" class="flex items-center gap-1">
                <code class="text-[10px] text-n-slate-9 truncate max-w-xs">
                  {{ embedSnippet(form) }}
                </code>
                <button
                  v-tooltip.top="$t('FORMS_SETTINGS.COPY_EMBED')"
                  type="button"
                  class="p-0.5 text-n-slate-9 hover:text-n-slate-12 transition-colors"
                  @click.stop="copyToClipboard(embedSnippet(form))"
                >
                  <span class="i-lucide-copy w-3 h-3" />
                </button>
              </div>

              <!-- UTM Link Builder -->
              <div
                v-if="utmExpandedId === form.id"
                class="mt-2 p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
              >
                <p class="text-xs font-medium text-n-slate-12 mb-2">
                  {{ $t('FORMS_SETTINGS.UTM_BUILDER.TITLE') }}
                </p>
                <div class="grid grid-cols-2 gap-2 mb-2">
                  <div
                    v-for="key in [
                      'source',
                      'medium',
                      'campaign',
                      'term',
                      'content',
                    ]"
                    :key="key"
                  >
                    <label
                      class="block text-[10px] text-n-slate-10 mb-0.5 uppercase"
                      >{{
                        $t(`FORMS_SETTINGS.UTM_BUILDER.${key.toUpperCase()}`)
                      }}</label
                    >
                    <input
                      v-model="utmParams[key]"
                      type="text"
                      class="w-full text-xs border border-n-weak rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-woot-500"
                      :placeholder="
                        $t(
                          `FORMS_SETTINGS.UTM_BUILDER.${key.toUpperCase()}_PLACEHOLDER`
                        )
                      "
                    />
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <code class="text-[10px] text-n-slate-11 truncate flex-1">{{
                    buildUtmUrl(form)
                  }}</code>
                  <button
                    type="button"
                    class="shrink-0 text-xs px-2 py-1 bg-woot-500 text-white rounded hover:bg-woot-600 transition-colors"
                    @click.stop="copyToClipboard(buildUtmUrl(form))"
                  >
                    {{ $t('FORMS_SETTINGS.UTM_BUILDER.COPY') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <Button
              v-tooltip.top="$t('FORMS_SETTINGS.UTM_BUILDER.TOGGLE')"
              icon="i-lucide-link"
              slate
              xs
              faded
              @click="toggleUtmBuilder(form.id)"
            />
            <Button
              v-tooltip.top="
                form.active
                  ? $t('FORMS_SETTINGS.DEACTIVATE')
                  : $t('FORMS_SETTINGS.ACTIVATE')
              "
              :icon="form.active ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              slate
              xs
              faded
              @click="toggleFormActive(form)"
            />
            <Button
              v-tooltip.top="$t('FORMS_SETTINGS.EDIT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="navigateToFormBuilder(form.id)"
            />
            <Button
              v-tooltip.top="$t('FORMS_SETTINGS.DELETE')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="deleteForm(form)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <FormCreateModal
    v-if="showCreateModal"
    :ticket-categories="ticketCategories"
    @close="showCreateModal = false"
    @created="handleFormCreated"
  />
</template>
